import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectLatestRoute, selectLoadingLatestRoute } from '../../../features/tripcenter/routeSlice';
import { setWorkingRules, selectWorkingRules } from '../../../features/tripcenter/googleRouteMakerSlice';
import PropTypes from 'prop-types';

// MUI components
import {
    Box,
    Typography,
    TextField,
    Checkbox,
    InputAdornment,
    Grid,
    Alert,
    Button,
    CircularProgress
} from '@mui/material';

// Default values function (same as in modal)
const defaultValues = (routeDuration, routeLength) => ({
    drivingStartTime: '00:00',
    drivingEndTime: '23:59',
    speedLimit: 60,
    maxHaltTime: 3,
    routeViolationThreshold: 2,

/* minGeneratorHours: 0.5 * routeDuration,
    minGeneratorHoursPercentage: 50,
    minGeneratorDistance: 0.5 * routeLength,
    minGeneratorDistancePercentage: 50,
    minTemperature: 15,
    maxTemperature: 25,
*/
    minFuelEfficiency: 3,
    maxFuelConsumption: 100,
});

const RouteRules = ({ routeLength, routeDuration }) => {
    const latestRoute = useSelector(selectLatestRoute);
    const loadingLatestRoute = useSelector(selectLoadingLatestRoute);
    const workingRules = useSelector(selectWorkingRules);
    const dispatch = useDispatch();
    const [finalRules, setFinalRules] = useState({});
    const [error, setError] = useState('');

    useEffect(() => {
        console.log("RouteRules: Getting latest route", latestRoute);
    }, [latestRoute]);



    useEffect(() => {
        // Handle initialization based on mode
        if (!loadingLatestRoute && latestRoute?.rules) {
            const mileage = latestRoute?.rules?.minFuelEfficiency || 3;
            const newRules = {
                ...latestRoute.rules,
                minFuelEfficiency: mileage,
                maxFuelConsumption: routeLength
                    ? Math.round(routeLength / mileage)
                    : 100
            };

            console.log("RouteRules: Setting final rules from latest route", newRules);
            setFinalRules(newRules);
            dispatch(setWorkingRules(newRules));
        }
    }, [dispatch, latestRoute, loadingLatestRoute, routeLength]);

    // Copy all the validation functions from RouteRulesModal
    const validateTimeRange = (startTime, endTime) => {
        if (!startTime || !endTime) return true;
        const start = startTime.split(':').map(Number);
        const end = endTime.split(':').map(Number);
        if (start[0] < end[0]) return true;
        if (start[0] > end[0]) return false;
        return start[1] <= end[1];
    };

    const validateTemperatureRange = (min, max) => {
        if (min === max) {
            return 'Minimum and maximum temperatures cannot be the same';
        }
        if (min > max) {
            return 'Minimum temperature must be lower than maximum temperature';
        }
        if (min === 0 && max === 0) {
            return 'Temperature range cannot be 0-0';
        }
        return '';
    };

    // Modify handleCheckboxChange
    const handleCheckboxChange = (rule) => {
        setError('');
        const defaultsToUse = defaultValues(routeDuration, routeLength);
        let newRules = { ...finalRules };

        if (rule === 'drivingStartTime' || rule === 'drivingEndTime') {
            const shouldAdd = !(finalRules.drivingStartTime || finalRules.drivingEndTime);
            if (shouldAdd) {
                newRules.drivingStartTime = defaultsToUse.drivingStartTime;
                newRules.drivingEndTime = defaultsToUse.drivingEndTime;
            } else {
                delete newRules.drivingStartTime;
                delete newRules.drivingEndTime;
            }
        } else if (rule === 'minGeneratorHours') {
            if (rule in newRules) {
                delete newRules.minGeneratorHours;
                delete newRules.minGeneratorHoursPercentage;
            } else {
                newRules.minGeneratorHours = defaultsToUse.minGeneratorHours;
                newRules.minGeneratorHoursPercentage = defaultsToUse.minGeneratorHoursPercentage;
            }
        } else if (rule === 'minGeneratorDistance') {
            if (rule in newRules) {
                delete newRules.minGeneratorDistance;
                delete newRules.minGeneratorDistancePercentage;
            } else {
                newRules.minGeneratorDistance = defaultsToUse.minGeneratorDistance;
                newRules.minGeneratorDistancePercentage = defaultsToUse.minGeneratorDistancePercentage;
            }
        } else if (rule === 'minTemperature' || rule === 'maxTemperature') {
            if ('minTemperature' in newRules) {
                delete newRules.minTemperature;
                delete newRules.maxTemperature;
            } else {
                newRules.minTemperature = defaultsToUse.minTemperature;
                newRules.maxTemperature = defaultsToUse.maxTemperature;
            }
        } else if (rule === 'minFuelEfficiency') {
            if (rule in newRules) {
                delete newRules.minFuelEfficiency;
                delete newRules.maxFuelConsumption;
            } else {
                newRules.minFuelEfficiency = defaultsToUse.minFuelEfficiency;
                newRules.maxFuelConsumption = routeLength
                    ? Math.round(routeLength / defaultsToUse.minFuelEfficiency)
                    : defaultsToUse.maxFuelConsumption;
            }
        } else {
            if (rule in newRules) {
                delete newRules[rule];
            } else {
                newRules[rule] = defaultsToUse[rule];
            }
        }

        // Update local state and dispatch to Redux immediately with the new rules
        setFinalRules(newRules);
        dispatch(setWorkingRules(newRules));
    };

    // Modify handleChange similarly
    const handleChange = (rule, value) => {
        setError('');

        if (!routeLength && ['minGeneratorDistance', 'minFuelEfficiency'].includes(rule)) {
            console.warn(`Cannot update ${rule} without route length`);
            return;
        }
        if (!routeDuration && ['minGeneratorHours'].includes(rule)) {
            console.warn(`Cannot update ${rule} without route duration`);
            return;
        }

        if (value === '' || value === 0) {
            handleCheckboxChange(rule);
            return;
        }

        if (rule === 'drivingStartTime' || rule === 'drivingEndTime') {
            const isValid = validateTimeRange(
                rule === 'drivingStartTime' ? value : finalRules.drivingStartTime,
                rule === 'drivingEndTime' ? value : finalRules.drivingEndTime
            );

            if (!isValid) {
                setError('Start time must be before end time');
                return;
            }
        }

        if (rule === 'minTemperature' || rule === 'maxTemperature') {
            const newMin = rule === 'minTemperature' ? value : finalRules.minTemperature;
            const newMax = rule === 'maxTemperature' ? value : finalRules.maxTemperature;
            const tempError = validateTemperatureRange(newMin, newMax);
            if (tempError) {
                setError(tempError);
                return;
            }
        }

        let newRules = { ...finalRules };

        if (rule === 'minGeneratorHours' || rule === 'minGeneratorHoursPercentage') {
            if (rule === 'minGeneratorHours') {
                newRules.minGeneratorHours = value;
                newRules.minGeneratorHoursPercentage = routeDuration ? Math.round(value / routeDuration * 100) : 0;
            } else {
                newRules.minGeneratorHoursPercentage = value;
                newRules.minGeneratorHours = Math.round(routeDuration * value / 100);
            }
        } else if (rule === 'minGeneratorDistance' || rule === 'minGeneratorDistancePercentage') {
            if (rule === 'minGeneratorDistance') {
                newRules.minGeneratorDistance = value;
                newRules.minGeneratorDistancePercentage = routeLength ? Math.round(value / routeLength * 100) : 0;
            } else {
                newRules.minGeneratorDistancePercentage = value;
                newRules.minGeneratorDistance = Math.round(routeLength * value / 100);
            }
        } else if (rule === 'minFuelEfficiency' || rule === 'maxFuelConsumption') {
            if (rule === 'minFuelEfficiency') {
                newRules.minFuelEfficiency = value;
                newRules.maxFuelConsumption = routeLength ? Math.round(routeLength / value) : 0;
            } else {
                newRules.maxFuelConsumption = value;
                newRules.minFuelEfficiency = routeLength ? Math.round(routeLength / value * 10) / 10 : 0;
            }
        } else {
            newRules[rule] = value;
        }

        // Update local state and dispatch to Redux immediately with the new rules
        setFinalRules(newRules);
        dispatch(setWorkingRules(newRules));
    };

    if (loadingLatestRoute) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={60} />
                <Typography sx={{ mt: 2 }}>Loading Rules data...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            position: 'relative',
            p: { xs: 2, md: 3 }
        }}>
            {/* Content Container */}
            <Box sx={{
                flex: 1,
                maxWidth: '900px',
                width: '100%',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Set Rules
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Define rules for monitoring this route such as speed limits, fuel efficiency targets, and allowed deviations.
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Rules Form - Scrollable Container */}
                <Box sx={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    pb: 2,
                    '& .rule-container': {
                        bgcolor: 'background.paper',
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover'
                        }
                    },
                    '& .rule-content': {
                        display: 'grid',
                        gridTemplateColumns: '50px minmax(180px, 220px) 1fr',
                        alignItems: 'center',
                        gap: 2
                    }
                }}>
                    {/* Route Violation Threshold */}
                    <Box className="rule-container">
                        <Box className="rule-content">
                            <Checkbox
                                checked={!!finalRules.routeViolationThreshold}
                                onChange={() => handleCheckboxChange('routeViolationThreshold')}
                                sx={{ ml: -1 }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                Route Violation Threshold
                            </Typography>
                            <TextField
                                type="number"
                                size="small"
                                value={finalRules.routeViolationThreshold || 2}
                                onChange={(e) => handleChange('routeViolationThreshold', Number(e.target.value))}
                                disabled={!finalRules.routeViolationThreshold}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">km</InputAdornment>,
                                }}
                                sx={{ width: '200px' }}
                            />
                        </Box>
                    </Box>


                    {/* Maximum Halt Time */}
                    <Box className="rule-container">
                        <Box className="rule-content">
                            <Checkbox
                                checked={!!finalRules.maxHaltTime}
                                onChange={() => handleCheckboxChange('maxHaltTime')}
                                sx={{ ml: -1 }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                Maximum Halt Time
                            </Typography>
                            <TextField
                                type="number"
                                size="small"
                                value={finalRules.maxHaltTime || 3}
                                onChange={(e) => handleChange('maxHaltTime', Number(e.target.value))}
                                disabled={!finalRules.maxHaltTime}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">Hours</InputAdornment>,
                                }}
                                sx={{ width: '200px' }}
                            />
                        </Box>
                    </Box>

                    {/* Speed Limit */}
                    <Box className="rule-container">
                        <Box className="rule-content">
                            <Checkbox
                                checked={!!finalRules.speedLimit}
                                onChange={() => handleCheckboxChange('speedLimit')}
                                sx={{ ml: -1 }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                Speed Limit
                            </Typography>
                            <TextField
                                type="number"
                                size="small"
                                value={finalRules.speedLimit || 60}
                                onChange={(e) => handleChange('speedLimit', Number(e.target.value))}
                                disabled={!finalRules.speedLimit}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">km/h</InputAdornment>,
                                }}
                                sx={{ width: '200px' }}
                            />
                        </Box>
                    </Box>

                    {/* Driving Hours */}
                    <Box className="rule-container">
                        <Box className="rule-content">
                            <Checkbox
                                checked={!!finalRules.drivingStartTime}
                                onChange={() => handleCheckboxChange('drivingStartTime')}
                                sx={{ ml: -1 }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                Driving Hours
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                '& .MuiTextField-root': {
                                    flex: 1,
                                    maxWidth: '160px'
                                }
                            }}>
                                <TextField
                                    type="time"
                                    size="small"
                                    label="Start Time"
                                    value={finalRules.drivingStartTime || '00:00'}
                                    onChange={(e) => handleChange('drivingStartTime', e.target.value)}
                                    disabled={!finalRules.drivingStartTime}
                                />
                                <Typography sx={{ color: 'text.secondary' }}>to</Typography>
                                <TextField
                                    type="time"
                                    size="small"
                                    label="End Time"
                                    value={finalRules.drivingEndTime || '23:59'}
                                    onChange={(e) => handleChange('drivingEndTime', e.target.value)}
                                    disabled={!finalRules.drivingEndTime}
                                />
                            </Box>
                        </Box>
                    </Box>



                    {/* Fuel Rules */}
                    <Box className="rule-container">
                        <Box className="rule-content">
                            <Checkbox
                                checked={'minFuelEfficiency' in finalRules}
                                onChange={() => handleCheckboxChange('minFuelEfficiency')}
                                sx={{ ml: -1 }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                Fuel Rules
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                '& .MuiTextField-root': {
                                    flex: 1,
                                    maxWidth: '160px'
                                }
                            }}>
                                <TextField
                                    type="number"
                                    size="small"
                                    label="Min Efficiency"
                                    value={finalRules.minFuelEfficiency || 3}
                                    onChange={(e) => handleChange('minFuelEfficiency', Number(e.target.value))}
                                    disabled={!('minFuelEfficiency' in finalRules)}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">km/L</InputAdornment>,
                                    }}
                                />
                                <Typography sx={{ color: 'text.secondary' }}>or</Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    label="Max Consumption"
                                    value={finalRules.maxFuelConsumption || 100}
                                    onChange={(e) => handleChange('maxFuelConsumption', Number(e.target.value))}
                                    disabled={!('minFuelEfficiency' in finalRules)}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">L</InputAdornment>,
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>


                </Box>
            </Box>
        </Box>
    );
};

RouteRules.propTypes = {
    existingRules: PropTypes.object,
    routeLength: PropTypes.number,
    routeDuration: PropTypes.number
};

export default RouteRules; 